import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { ArrowLeft } from 'react-feather';
import { Page, PageHeader, Card, Button, Field, TextInput, Select, Badge } from '../../../../../ui';
import SegmentedControl from '../ui-pending/SegmentedControl';
import '../ui-pending/ui-pending.scss';
import axios from 'axios';
import { toast } from 'react-toastify';
import qaFormData from './qaform.json';
import { WEB_API_BASEURL } from '../../../../../Api/api-constant';
import { getGraded, courseLevel } from '../../../../../Api/apiUtils';
import './qareports.scss';
import { ConfigContext } from '../../../../../Api/global/ApiContext';
import { trackEvent, MixpanelEvents } from '../../../../../utils/mixpanelTracking';
import RichTextEditor from './RichTextEditor';
import { isRichTextEmpty } from './richTextUtils';

type QaQuestion = {
  id: string;
  title: string;
  type: string;
  description?: string;
  placeholder?: string;
  max_marks?: number;
  evaluation_points?: string[];
  options?: {
    marks?: number[];
    breakup?: string;
    na_label?: string;
    scale?: number[];
    rating_descriptions?: Record<string, string>;
  };
};

type QaSection = {
  section_title: string;
  section_total?: number;
  description?: string;
  questions?: QaQuestion[];
  content?: Array<{ type?: string; description?: string; url?: string; label?: string }>;
};

type QaFormPage = {
  page_number: number;
  page_title?: string;
  fields?: string[];
  sections?: QaSection[];
  actions?: { submit_button?: { label: string; action?: string } };
};

type QaFormConfig = {
  title?: string;
  auditors?: string[];
  auditCategories?: string[];
  maxScore?: number;
  pages: QaFormPage[];
};

const QaReports: React.FC = () => {
  const [searchParams] = useSearchParams();
  const classId = searchParams.get('classId');
  const formData = qaFormData as QaFormConfig;
  const [currentPage, setCurrentPage] = useState(1);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [autoPopulatedFields, setAutoPopulatedFields] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const config = useContext(ConfigContext);
  const { grmpcourses = [] } = config || {};
  // Get lists from JSON configuration
  const auditorsList = formData.auditors || [];
  const auditCategoryList = formData.auditCategories || [];
  
  const totalPages = formData.pages.length;
  const currentPageData = formData.pages.find((page) => page.page_number === currentPage);

  // Function to fetch graded certificate
  const fetchGradedCertificate = async (studentId: any, courseId: any): Promise<{ certificate: string; courseName?: string }> => {
    try {
      const response = await getGraded(studentId, courseId, "getCertification");
      const certificate = response?.gradedCertification || 'No certificate';
      const courseName = response?.courseName;
      return {
        certificate,
        courseName
      };
    } catch (error) {
      console.error('Error fetching graded certificate:', error);
      return {
        certificate: 'No certificate'
      };
    }
  };

  // Auto-populate form fields from eventDetails stored in localStorage
  useEffect(() => {
    const loadFormData = async () => {
    if (classId) {
      setIsLoading(true);
      try {
        // Retrieve eventDetails from localStorage using classId as key
        const storedEventDetails = localStorage.getItem(`qaEventDetails_${classId}`);
        
        if (storedEventDetails) {
          const eventDetails = JSON.parse(storedEventDetails);
          
          // Map eventDetails to form fields
          const autoPopulatedValues: Record<string, any> = {};
          
          // Map fields from eventDetails to form field names
          if (eventDetails.teacherName) {
            autoPopulatedValues['Teacher'] = eventDetails.teacherName;
          }
          if (eventDetails.studentName) {
            autoPopulatedValues['Student'] = eventDetails.studentName;
          }
          if (eventDetails.slotDate) {
            autoPopulatedValues['Date of Class'] = eventDetails.slotDate;
          }
          // Auto-populate Class Number if available (from student calendar)
          if (eventDetails.classNumber) {
            autoPopulatedValues['Class Number'] = eventDetails.classNumber;
          }
          if (eventDetails.lessonNumber) {
            autoPopulatedValues['Lesson Number'] = eventDetails.lessonNumber;
          }
          if (eventDetails.ageGroup) {
            autoPopulatedValues['Age'] = eventDetails.ageGroup;
          }
          if (eventDetails.studentTimezone){
            autoPopulatedValues['Timezone'] = eventDetails.studentTimezone;
          }
          // Set Date of Audit to current date
          const today = new Date();
          const formattedDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format
          autoPopulatedValues['Date of Audit'] = formattedDate;
          
          // Fetch graded certificate if studentId and courseId are available
          // This API also returns courseName which we can use
          if (eventDetails.studentId && eventDetails.courseId) {
            const certificateResponse = await fetchGradedCertificate(eventDetails.studentId, eventDetails.courseId);
            autoPopulatedValues['Graded Certification'] = certificateResponse.certificate;
            
            // Use courseName from graded certificate response
            if (certificateResponse.courseName) {
              autoPopulatedValues['Course'] = certificateResponse.courseName;
            } else if (eventDetails.courseName) {
              // Fallback to eventDetails.courseName
              autoPopulatedValues['Course'] = eventDetails.courseName;
            }
          } else {
            // If no certificate data available, set to "No certificate"
            autoPopulatedValues['Graded Certification'] = 'No certificate';
            // Use courseName from eventDetails if available
            if (eventDetails.courseName) {
              autoPopulatedValues['Course'] = eventDetails.courseName;
            }
          }
          
          // Fetch level name using courseLevel API
          if (eventDetails.studentUserId && eventDetails.courseId) {
            try {
              const levelResponse = await courseLevel(eventDetails.studentUserId, eventDetails.courseId);
              if (levelResponse?.status && levelResponse.status.length > 0 && levelResponse.status[0].level_name) {
                autoPopulatedValues['Level'] = levelResponse.status[0].level_name;
              } else if (eventDetails.level) {
                // Fallback to eventDetails.level if API doesn't return level_name
                autoPopulatedValues['Level'] = eventDetails.level;
              }
            } catch (error) {
              console.error('Error fetching course level:', error);
              // Fallback to eventDetails.level on error
              if (eventDetails.level) {
                autoPopulatedValues['Level'] = eventDetails.level;
              }
            }
          } else if (eventDetails.level) {
            autoPopulatedValues['Level'] = eventDetails.level;
          }
          
          // Set form values
          setFormValues(autoPopulatedValues);
          
          // Track which fields were auto-populated
          const autoPopulatedFieldSet = new Set(Object.keys(autoPopulatedValues));
          setAutoPopulatedFields(autoPopulatedFieldSet);
          
          // Track QA Report page view
          trackEvent(MixpanelEvents.QA_REPORT_PAGE_VIEW, {
            classId: classId,
            teacherUserId: eventDetails.teacherUserId,
            studentUserId: eventDetails.studentUserId,
            studentName: eventDetails.studentName,
            teacherName: eventDetails.teacherName
          });
          
          // Clean up localStorage after use (optional - you can keep it if needed)
          // localStorage.removeItem(`qaEventDetails_${classId}`);
        }
      } catch (error) {
        console.error('Error parsing eventDetails from localStorage:', error);
      } finally {
        setIsLoading(false);
      }
    }
    };

    loadFormData();
  }, [classId]);

  const handleInputChange = (fieldId: string, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
    // Clear validation error when user starts typing
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (questionId: string, checked: boolean) => {
    if (checked) {
      setFormValues(prev => ({
        ...prev,
        [questionId]: 'N/A'
      }));
    } else {
      setFormValues(prev => {
        const newValues = { ...prev };
        delete newValues[questionId];
        return newValues;
      });
    }
    // Clear validation error when user selects N/A
    if (validationErrors[questionId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  // Validation function to check if current page fields are filled
  const validateCurrentPage = (): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    
    if (currentPage === 1 && currentPageData?.fields) {
      // Validate page 1 fields
      currentPageData.fields.forEach((field: string) => {
        if (!formValues[field] || formValues[field] === '') {
          errors[field] = `${field} is required`;
        }
      });
    } else if (currentPage > 1 && currentPageData?.sections) {
      // Validate questions on other pages
      currentPageData.sections.forEach((section: any) => {
        if (section.questions) {
          section.questions.forEach((question: any) => {
            const questionId = question.id;
            const value = formValues[questionId];
            
            if (question.type === 'rating_with_na') {
              if (!value || value === '') {
                errors[questionId] = `${question.title} is required`;
              }
            } else if (question.type === 'long_text') {
              if (isRichTextEmpty(value)) {
                errors[questionId] = `${question.title} is required`;
              }
            }
          });
        }
      });
    }
    
    setValidationErrors(errors);
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const handleNext = () => {
    const validationResult = validateCurrentPage();
    if (!validationResult.isValid) {
      toast.error('Please fill all required fields before proceeding');
      // Track validation error
      trackEvent(MixpanelEvents.QA_REPORT_VALIDATION_ERROR, {
        classId: classId,
        currentPage: currentPage,
        errors: Object.keys(validationResult.errors),
        errorCount: Object.keys(validationResult.errors).length
      });
      window.scrollTo(0, 0);
      return;
    }
    
    if (currentPage < totalPages) {
      // Track next page navigation
      trackEvent(MixpanelEvents.QA_REPORT_NEXT_PAGE, {
        classId: classId,
        fromPage: currentPage,
        toPage: currentPage + 1,
        totalPages: totalPages
      });
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      // Track previous page navigation
      trackEvent(MixpanelEvents.QA_REPORT_PREVIOUS_PAGE, {
        classId: classId,
        fromPage: currentPage,
        toPage: currentPage - 1,
        totalPages: totalPages
      });
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    window.close();
  };

  // Helper function to get question title by ID
  const getQuestionTitleById = (questionId: string): string | null => {
    for (const page of formData.pages) {
      if (page.sections) {
        for (const section of page.sections) {
          if (section.questions) {
            for (const question of section.questions) {
              if (question.id === questionId) {
                return question.title;
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Transform form values to API format
  const transformFormDataToAPI = (): any => {
    if (!classId) {
      throw new Error('Class ID is required');
    }

    // Get eventDetails from localStorage
    const storedEventDetails = localStorage.getItem(`qaEventDetails_${classId}`);
    if (!storedEventDetails) {
      throw new Error('Event details not found');
    }

    const eventDetails = JSON.parse(storedEventDetails);
    const teacherUserId = eventDetails.teacherUserId || localStorage.getItem('teacherProfileUserId');
    const studentUserId = eventDetails.studentUserId;

    if (!teacherUserId || !studentUserId) {
      throw new Error('Teacher User ID or Student User ID is missing');
    }

    // Build feedback object
    const feedback: any = {};

    // Add basic fields from page 1
    const basicFields = [
      'Auditor',
      'Date of Audit',
      'Teacher',
      'Student',
      'Graded Certification',
      'Age',
      'Timezone',
      'Course',
      'Level',
      'Date of Class',
      'Class Number',
      'Lesson Number',
      'Audit Category'
    ];

    basicFields.forEach(field => {
      if (formValues[field] !== undefined && formValues[field] !== '') {
        feedback[field] = formValues[field];
      }
    });

    // Process questions from all pages
    for (const page of formData.pages) {
      if (page.sections) {
        for (const section of page.sections) {
          if (section.questions) {
            for (const question of section.questions) {
              const questionId = question.id;
              const questionTitle = question.title;
              const value = formValues[questionId];

              if (value !== undefined && value !== '') {
                if (question.type === 'rating_with_na') {
                  // For rating questions, create object with score, na, and rating_descriptions
                  const isNA = value === 'N/A';
                  const score = isNA ? null : parseInt(value, 10);
                  
                  // Get the full rating_descriptions array from question options
                  const ratingDescriptions = ('options' in question && question.options?.rating_descriptions) 
                    ? question.options.rating_descriptions 
                    : {};
                  
                  feedback[questionTitle] = {
                    score: isNA ? null : score,
                    na: isNA,
                    rating_descriptions: ratingDescriptions
                  };
                } else if (question.type === 'long_text') {
                  // For long text, add as comment or separate field
                  // Check if there's already a rating question with the same title
                  if (feedback[questionTitle] && typeof feedback[questionTitle] === 'object') {
                    feedback[questionTitle].comment = value;
                  } else {
                    // If it's a standalone text field, add it directly
                    feedback[questionTitle] = value;
                  }
                }
              }
            }
          }
        }
      }
    }

    // Handle "Overall Feedback" if it exists (from long_text fields like "Positives" and "Areas of Improvement")
    const positives = formValues['audit_positives'];
    const areasOfImprovement = formValues['areas_of_improvement'];
    
    if (positives || areasOfImprovement) {
      let overallFeedback = '';
      if (positives) {
        overallFeedback += `Positives: ${positives}`;
      }
      if (areasOfImprovement) {
        if (overallFeedback) overallFeedback += '\n\n';
        overallFeedback += `Areas of Improvement: ${areasOfImprovement}`;
      }
      if (overallFeedback) {
        feedback['Overall Feedback'] = overallFeedback;
      }
    }

    // Calculate total score percentage
    let totalScore = 0;
    let scoreCount = 0;
    let outOf = 0;
    let totalScorePercentage = 0;

    const maxScore = 10;
    for (const page of formData.pages) {
      if (page.sections) {
        for (const section of page.sections) {
          if (section.questions) {
            for (const question of section.questions) {
              if (question.type === 'rating_with_na') {
                const questionId = question.id;
                const value = formValues[questionId];
                
                if (value !== undefined && value !== '' && value !== 'N/A') {
                  const score = parseInt(value, 10);
                  if (!isNaN(score)) {
                    totalScore += score;
                    scoreCount++;
                  }
                }
              }
            }
          }
        }
      }
    }
    totalScorePercentage = scoreCount > 0 
      ? Math.round((totalScore / scoreCount / maxScore) * 100) 
      : 0;
    outOf = scoreCount * maxScore;

    feedback['QA Type'] = 'Academic QA';
    feedback['totalScorePercentage'] = totalScorePercentage;
    feedback['totalscore'] = totalScore;
    feedback['outOf'] = outOf;

    return {
      classId: classId,
      teacherUserId: teacherUserId.toString(),
      studentUserId: studentUserId.toString(),
      qaType: 'ACADEMIC',
      feedback: feedback
    };
  };

  // Validate all pages before submission
  const validateAllPages = (): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    
    // Validate page 1
    const page1 = formData.pages.find(page => page.page_number === 1);
    if (page1?.fields) {
      page1.fields.forEach((field: string) => {
        if (!formValues[field] || formValues[field] === '') {
          errors[field] = `${field} is required`;
        }
      });
    }
    
    // Validate all other pages
    for (const page of formData.pages) {
      if (page.page_number > 1 && page.sections) {
        for (const section of page.sections) {
          if (section.questions) {
            for (const question of section.questions) {
              const questionId = question.id;
              const value = formValues[questionId];
              
              if (question.type === 'rating_with_na') {
                if (!value || value === '') {
                  errors[questionId] = `${question.title} is required`;
                }
              } else if (question.type === 'long_text') {
                if (isRichTextEmpty(value)) {
                  errors[questionId] = `${question.title} is required`;
                }
              }
            }
          }
        }
      }
    }
    
    setValidationErrors(errors);
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const handleSubmit = () => {
    // Validate all pages before submission
    const validationResult = validateAllPages();
    if (!validationResult.isValid) {
      toast.error('Please fill all required fields before submitting');
      // Track validation error
      trackEvent(MixpanelEvents.QA_REPORT_VALIDATION_ERROR, {
        classId: classId,
        currentPage: currentPage,
        errors: Object.keys(validationResult.errors),
        errorCount: Object.keys(validationResult.errors).length
      });
      // Navigate to first page with errors
      const firstErrorField = Object.keys(validationResult.errors)[0];
      if (firstErrorField) {
        // Find which page contains the first error
        for (const page of formData.pages) {
          if (page.page_number === 1 && page.fields?.includes(firstErrorField)) {
            setCurrentPage(1);
            break;
          } else if (page.sections) {
            for (const section of page.sections) {
              if (section.questions?.some((q: any) => q.id === firstErrorField)) {
                setCurrentPage(page.page_number);
                break;
              }
            }
          }
        }
      }
      window.scrollTo(0, 0);
      return;
    }

    // Show confirmation modal
    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    setShowConfirmModal(false);
    
    try {
      setIsSubmitting(true);

      // Transform form data to API format
      const apiPayload = transformFormDataToAPI();

      // Make API call
      const response = await axios.post(
        `${WEB_API_BASEURL}/admin/teacher/quality/analysis/save`,
        apiPayload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success('QA Report submitted successfully!');
        console.log('Form submitted successfully:', response.data);

        const fb = apiPayload.feedback;
        let eventDetails: any = {};
        try {
          const stored = localStorage.getItem(`qaEventDetails_${classId}`);
          if (stored) eventDetails = JSON.parse(stored);
        } catch (_) {}

        const studentDetails = {
          studentName: fb?.Student ?? eventDetails?.studentName ?? '',
          teacherName: fb?.Teacher ?? eventDetails?.teacherName ?? '',
          classNumber: fb?.['Class Number'] ?? eventDetails?.classNumber ?? '',
          lessonNumber: fb?.['Lesson Number'] ?? eventDetails?.lessonNumber ?? '',
          courseName: fb?.Course ?? eventDetails?.courseName ?? '',
          courseLevel: fb?.Level ?? eventDetails?.level ?? '',
          certificateChoice: fb?.['Graded Certification'] ?? '',
          gradeLevel: fb?.Level ?? eventDetails?.level ?? '',
          ageGroup: fb?.Age ?? eventDetails?.ageGroup ?? '',
          gender: eventDetails?.gender ?? '',
          studentEmail: eventDetails?.studentEmail ?? '',
          studentPhone: eventDetails?.studentPhone ?? '',
        };

        trackEvent(MixpanelEvents.QA_REPORT_SUBMITTED, {
          qaType: 'Academic QA',
          classId: classId,
          teacherUserId: apiPayload.teacherUserId,
          studentUserId: apiPayload.studentUserId,
          totalScorePercentage: apiPayload.feedback.totalScorePercentage,
          totalScore: apiPayload.feedback.totalscore,
          outOf: apiPayload.feedback.outOf,
          auditor: apiPayload.feedback.Auditor,
          auditCategory: apiPayload.feedback['Audit Category'],
          studentDetails,
        });
        
        // Close the tab after successful submission
        setTimeout(() => {
          window.close();
        }, 1000); // Small delay to show success message
      }
    } catch (error: any) {
      console.error('Error submitting QA report:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit QA report';
      toast.error(errorMessage);

      let eventDetails: any = {};
      try {
        const stored = localStorage.getItem(`qaEventDetails_${classId}`);
        if (stored) eventDetails = JSON.parse(stored);
      } catch (_) {}
      const studentDetails = {
        studentFullName: eventDetails?.studentName ?? '',
        teacherFullName: eventDetails?.teacherName ?? '',
        classNumber: eventDetails?.classNumber ?? '',
        lessonNumber: eventDetails?.lessonNumber ?? '',
        courseName: eventDetails?.courseName ?? '',
        courseLevel: eventDetails?.level ?? '',
        certificateChoice: '',
        gradeLevel: eventDetails?.level ?? '',
        ageGroup: eventDetails?.ageGroup ?? '',
        gender: eventDetails?.gender ?? '',
        studentEmail: eventDetails?.studentEmail ?? '',
        studentPhone: eventDetails?.studentPhone ?? '',
      };

      trackEvent(MixpanelEvents.QA_REPORT_SUBMISSION_FAILED, {
        classId: classId,
        errorMessage: errorMessage,
        errorStatus: error.response?.status,
        errorData: error.response?.data,
        studentDetails,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageTitle =
    currentPage === 1
      ? formData.title || 'PAID CLASS - Quality Analysis'
      : currentPageData?.page_title || formData.title || 'PAID CLASS - Quality Analysis';

  return (
    <Page className="tp-qa">
      <PageHeader
        eyebrow="Academic QA"
        title={pageTitle}
        subtitle={currentPage === 1 ? currentPageData?.page_title : undefined}
        actions={
          currentPage > 1 ? (
            <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden />} onClick={handlePrevious}>
              Back
            </Button>
          ) : undefined
        }
      />

      {currentPage === 1 && currentPageData?.fields && (
        <Card title="Class details">
          <div className="tp-qa__grid">
            {currentPageData.fields.map((field, index) => (
              <Field key={index} label={field} required error={validationErrors[field]}>
                {(control) =>
                  field === 'Auditor' ? (
                    <Select
                      {...control}
                      value={formValues[field] || ''}
                      onChange={(e) => handleInputChange(field, e.target.value)}
                      required
                      options={[
                        { value: '', label: 'Select Auditor' },
                        ...auditorsList.map((auditor) => ({ value: auditor, label: auditor })),
                      ]}
                    />
                  ) : field === 'Audit Category' ? (
                    <Select
                      {...control}
                      value={formValues[field] || ''}
                      onChange={(e) => handleInputChange(field, e.target.value)}
                      required
                      options={[
                        { value: '', label: 'Select Audit Category' },
                        ...auditCategoryList.map((category) => ({ value: category, label: category })),
                      ]}
                    />
                  ) : (
                    <TextInput
                      {...control}
                      type="text"
                      value={formValues[field] || ''}
                      onChange={(e) => {
                        // Prevent editing if field is auto-populated
                        if (autoPopulatedFields.has(field)) {
                          return;
                        }
                        handleInputChange(field, e.target.value);
                      }}
                      placeholder={
                        field === 'Class Number' && !autoPopulatedFields.has('Class Number')
                          ? 'Please put class number from student calendar'
                          : `Enter ${field}`
                      }
                      readOnly={autoPopulatedFields.has(field)}
                      required
                    />
                  )
                }
              </Field>
            ))}
          </div>
        </Card>
      )}

      {/* Other Pages - Sections with Questions */}
      {currentPage > 1 && currentPageData?.sections && (
        <div className="tp-qa__sections">
          {currentPageData.sections.map((section, sectionIndex) => (
            <Card
              key={sectionIndex}
              title={section.section_title}
              subtitle={'description' in section && section.description ? section.description : undefined}
              actions={
                section.section_total != null ? <Badge tone="neutral">Total {section.section_total}</Badge> : undefined
              }
            >
              {/* Questions */}
              {section.questions && section.questions.map((question, qIndex) => (
                <div key={qIndex} className="tp-qa__question">
                  <h3 className="tp-qa__question-title">
                    {question.title}
                    <span className="tp-qa__required" aria-hidden>*</span>
                  </h3>

                  {/* Question Description */}
                  {'description' in question && question.description && (
                    <p className="tp-qa__text">{question.description}</p>
                  )}

                  {/* Evaluation Points */}
                  {'evaluation_points' in question && question.evaluation_points && question.evaluation_points.length > 0 && (
                    <div className="tp-qa__panel">
                      <p className="tp-qa__text">You are expected to observe how effectively the teacher:</p>
                      <ol className="tp-qa__list">
                        {question.evaluation_points.map((point: string, pointIndex: number) => (
                          <li key={pointIndex}>{point}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Rating with N/A */}
                  {question.type === 'rating_with_na' && 'options' in question && question.options && (
                    <div className="tp-qa__rating">
                      {/* Not Applicable Checkbox */}
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={formValues[question.id] === 'N/A'}
                            onChange={(e) => handleCheckboxChange(question.id, e.target.checked)}
                          />{' '}
                          {question.options.na_label || 'Not Applicable'}
                        </Label>
                      </FormGroup>

                      {/* Rating Scale 1-10 */}
                      {question.options.scale && (
                        <SegmentedControl
                          label={`${question.title} rating`}
                          value={formValues[question.id] ?? ''}
                          options={question.options.scale.map((score: number) => ({
                            value: score.toString(),
                            label: score,
                            disabled: formValues[question.id] === 'N/A',
                          }))}
                          onChange={(score) => {
                            if (formValues[question.id] !== 'N/A') {
                              handleInputChange(question.id, score);
                            }
                          }}
                        />
                      )}

                      {/* Rating Description */}
                      {formValues[question.id] && 
                       formValues[question.id] !== 'N/A' && 
                       question.options.rating_descriptions && 
                       question.options.rating_descriptions[formValues[question.id]] && (
                        <p className="tp-qa__rating-description">
                          {question.options.rating_descriptions[formValues[question.id]]}
                        </p>
                      )}
                      {validationErrors[question.id] && (
                        <span className="tp-qa__error" role="alert">
                          {validationErrors[question.id]}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Long Text Input */}
                  {question.type === 'long_text' && (
                    <>
                      <RichTextEditor
                        key={question.id}
                        value={formValues[question.id] || ''}
                        onChange={(html) => handleInputChange(question.id, html)}
                        placeholder={'placeholder' in question ? question.placeholder || '' : ''}
                        invalid={!!validationErrors[question.id]}
                      />
                      {validationErrors[question.id] && (
                        <span className="tp-qa__error" role="alert">
                          {validationErrors[question.id]}
                        </span>
                      )}
                    </>
                  )}

                  {/* External Link Content */}
                  {section.content && Array.isArray(section.content) && section.content.map((contentItem, contentIndex) => (
                    contentItem.type === 'external_link' && (
                      <div key={contentIndex} className="tp-qa__panel">
                        <p className="tp-qa__text">{contentItem.description}</p>
                        <a 
                          href={contentItem.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="tp-qa__link"
                        >
                          {contentItem.label}
                        </a>
                      </div>
                    )
                  ))}
                </div>
              ))}
            </Card>
          ))}
        </div>
      )}

      {/* Navigation Footer */}
      <div className="tp-qa__footer">
        <div className="tp-qa__footer-side">
          {currentPage > 1 && (
            <Button variant="secondary" onClick={handlePrevious}>
              Previous
            </Button>
          )}
        </div>
        <div className="tp-qa__footer-side">
          <span className="tp-qa__page-indicator">
            Page {currentPage} of {totalPages}
          </span>
          {currentPage < totalPages && (
            <Button variant="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          {currentPageData?.actions?.submit_button && (
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : currentPageData.actions.submit_button.label}
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)}>
        <ModalHeader toggle={() => setShowConfirmModal(false)}>
          Confirm Submission
        </ModalHeader>
        <ModalBody>
          Are you sure you want to submit this Academic QA report? Once submitted, the report will be saved and this tab will be closed.
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Confirm & Submit'}
          </Button>
        </ModalFooter>
      </Modal>
    </Page>
  );
};

export default QaReports;
